import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { serviceAPI, orderAPI, paymentAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import './Dashboard.css';

const STATUS_COLORS = {
  PENDING: 'warning', CONFIRMED: 'info', IN_PROGRESS: 'info',
  COMPLETED: 'success', CANCELLED: 'error', REFUNDED: 'error',
};

export default function SellerDashboard() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState('overview');
  const [services, setServices] = useState([]);
  const [orders, setOrders] = useState([]);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [onboarding, setOnboarding] = useState(false);

  useEffect(() => {
    if (searchParams.get('subscription') === 'success') toast.success('Subscription activated!');
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [svcRes, ordRes, balRes] = await Promise.all([
        serviceAPI.search({ sellerId: user.id, limit: 50 }),
        orderAPI.getAll({ role: 'seller', limit: 20 }),
        paymentAPI.getSellerBalance(),
      ]);
      setServices(svcRes.data.services || []);
      setOrders(ordRes.data.orders || []);
      setBalance(balRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleStripeOnboarding = async () => {
    setOnboarding(true);
    try {
      const { data } = await paymentAPI.createSellerAccount();
      window.location.href = data.url;
    } catch (err) {
      toast.error('Failed to start Stripe onboarding');
    } finally {
      setOnboarding(false);
    }
  };

  const handleToggleService = async (id, current) => {
    try {
      await serviceAPI.update(id, { isActive: !current });
      setServices(s => s.map(svc => svc.id === id ? { ...svc, isActive: !current } : svc));
      toast.success(current ? 'Service deactivated' : 'Service activated');
    } catch {
      toast.error('Failed to update service');
    }
  };

  const handleOrderStatus = async (id, status) => {
    try {
      await orderAPI.updateStatus(id, status);
      setOrders(o => o.map(ord => ord.id === id ? { ...ord, status } : ord));
      toast.success(`Order marked as ${status.toLowerCase()}`);
    } catch {
      toast.error('Failed to update order');
    }
  };

  const totalRevenue = orders.filter(o => ['CONFIRMED','COMPLETED'].includes(o.status))
    .reduce((s, o) => s + o.sellerAmount, 0);
  const totalOrders = orders.length;
  const completedOrders = orders.filter(o => o.status === 'COMPLETED').length;

  if (loading) return <div className="loading-page"><div className="spinner" /></div>;

  return (
    <div className="dashboard-page page">
      <div className="container">
        {/* Header */}
        <div className="dashboard-header">
          <div>
            <h1>Seller Dashboard</h1>
            <p className="text-light">Welcome back, {user.firstName}! Here's your business overview.</p>
          </div>
          <Link to="/dashboard/seller/create" className="btn btn-primary">+ Add New Service</Link>
        </div>

        {/* Stripe onboarding banner */}
        {balance?.needsOnboarding && (
          <div className="alert alert-warning">
            <strong>💳 Set up payouts:</strong> Connect your Stripe account to receive payments directly.
            <button className="btn btn-sm btn-primary ml-2" onClick={handleStripeOnboarding} disabled={onboarding}>
              {onboarding ? 'Opening...' : 'Connect Stripe →'}
            </button>
          </div>
        )}

        {/* Stats */}
        <div className="stats-grid">
          {[
            { label: 'Total Services', value: services.length, icon: '📦' },
            { label: 'Active Services', value: services.filter(s => s.isActive).length, icon: '✅' },
            { label: 'Total Orders', value: totalOrders, icon: '🛒' },
            { label: 'Earnings (Est.)', value: `$${totalRevenue.toFixed(2)}`, icon: '💰' },
          ].map(s => (
            <div key={s.label} className="stat-card">
              <div className="stat-icon">{s.icon}</div>
              <div className="stat-value">{s.value}</div>
              <div className="stat-label">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="tabs">
          {['overview', 'services', 'orders', 'payments'].map(t => (
            <button key={t} className={`tab ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)}>
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {tab === 'overview' && (
          <div>
            <h3 className="mb-2">Recent Orders</h3>
            {orders.slice(0, 5).length > 0 ? (
              <div className="table-wrapper">
                <table>
                  <thead><tr><th>Service</th><th>Customer</th><th>Amount</th><th>Status</th><th>Action</th></tr></thead>
                  <tbody>
                    {orders.slice(0, 5).map(o => (
                      <tr key={o.id}>
                        <td><Link to={`/orders/${o.id}`} className="text-primary">{o.service?.title}</Link></td>
                        <td>{o.buyer?.firstName} {o.buyer?.lastName}</td>
                        <td className="font-semibold">${o.sellerAmount?.toFixed(2)}</td>
                        <td><span className={`badge badge-${STATUS_COLORS[o.status]}`}>{o.status}</span></td>
                        <td>
                          {o.status === 'CONFIRMED' && (
                            <button className="btn btn-sm btn-secondary" onClick={() => handleOrderStatus(o.id, 'IN_PROGRESS')}>Start</button>
                          )}
                          {o.status === 'IN_PROGRESS' && (
                            <button className="btn btn-sm btn-primary" onClick={() => handleOrderStatus(o.id, 'COMPLETED')}>Complete</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <p className="text-light">No orders yet.</p>}

            <h3 className="mt-3 mb-2">Your Services</h3>
            {services.slice(0, 4).length > 0 ? (
              <div className="grid grid-4">
                {services.slice(0, 4).map(s => (
                  <div key={s.id} className="service-mini-card">
                    <div className="service-mini-status">
                      <span className={`badge badge-${s.isActive ? 'success' : 'warning'}`}>{s.isActive ? 'Active' : 'Inactive'}</span>
                    </div>
                    <div className="service-mini-body">
                      <h4>{s.title}</h4>
                      <p className="text-primary font-bold">${s.price?.toFixed(2)}</p>
                      <p className="text-light text-sm">{s.totalOrders} orders · ⭐ {s.avgRating?.toFixed(1)}</p>
                    </div>
                    <div className="service-mini-actions">
                      <Link to={`/dashboard/seller/edit/${s.id}`} className="btn btn-ghost btn-sm">Edit</Link>
                      <button className="btn btn-ghost btn-sm" onClick={() => handleToggleService(s.id, s.isActive)}>
                        {s.isActive ? 'Pause' : 'Activate'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <div className="empty-state-icon">📦</div>
                <h3>No services yet</h3>
                <p>Create your first service listing to start receiving orders.</p>
                <Link to="/dashboard/seller/create" className="btn btn-primary">+ Create Service</Link>
              </div>
            )}
          </div>
        )}

        {/* Services Tab */}
        {tab === 'services' && (
          <div>
            <div className="tab-header">
              <h3>All Services ({services.length})</h3>
              <Link to="/dashboard/seller/create" className="btn btn-primary btn-sm">+ New Service</Link>
            </div>
            {services.length > 0 ? (
              <div className="table-wrapper">
                <table>
                  <thead><tr><th>Title</th><th>Category</th><th>Price</th><th>Orders</th><th>Rating</th><th>Status</th><th>Actions</th></tr></thead>
                  <tbody>
                    {services.map(s => (
                      <tr key={s.id}>
                        <td><Link to={`/services/${s.id}`} className="text-primary font-semibold">{s.title}</Link></td>
                        <td>{s.category?.name}</td>
                        <td>${s.price?.toFixed(2)}</td>
                        <td>{s.totalOrders}</td>
                        <td>{s.avgRating > 0 ? `⭐ ${s.avgRating?.toFixed(1)}` : '—'}</td>
                        <td><span className={`badge badge-${s.isActive ? 'success' : 'warning'}`}>{s.isActive ? 'Active' : 'Paused'}</span></td>
                        <td>
                          <div className="flex gap-1">
                            <Link to={`/dashboard/seller/edit/${s.id}`} className="btn btn-ghost btn-sm">✏️</Link>
                            <button className="btn btn-ghost btn-sm" onClick={() => handleToggleService(s.id, s.isActive)}>
                              {s.isActive ? '⏸' : '▶️'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-state">
                <div className="empty-state-icon">📦</div>
                <h3>No services yet</h3>
                <Link to="/dashboard/seller/create" className="btn btn-primary">+ Create Your First Service</Link>
              </div>
            )}
          </div>
        )}

        {/* Orders Tab */}
        {tab === 'orders' && (
          <div>
            <h3 className="mb-2">All Orders ({orders.length})</h3>
            {orders.length > 0 ? (
              <div className="table-wrapper">
                <table>
                  <thead><tr><th>Order</th><th>Customer</th><th>Service</th><th>Amount</th><th>Status</th><th>Date</th><th>Action</th></tr></thead>
                  <tbody>
                    {orders.map(o => (
                      <tr key={o.id}>
                        <td><Link to={`/orders/${o.id}`} className="text-primary text-sm">#{o.id.slice(-6).toUpperCase()}</Link></td>
                        <td>{o.buyer?.firstName} {o.buyer?.lastName}</td>
                        <td className="text-sm">{o.service?.title}</td>
                        <td className="font-semibold">${o.sellerAmount?.toFixed(2)}</td>
                        <td><span className={`badge badge-${STATUS_COLORS[o.status]}`}>{o.status}</span></td>
                        <td className="text-sm text-light">{new Date(o.createdAt).toLocaleDateString()}</td>
                        <td>
                          {o.status === 'CONFIRMED' && <button className="btn btn-sm btn-secondary" onClick={() => handleOrderStatus(o.id, 'IN_PROGRESS')}>Start</button>}
                          {o.status === 'IN_PROGRESS' && <button className="btn btn-sm btn-primary" onClick={() => handleOrderStatus(o.id, 'COMPLETED')}>Complete</button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <div className="empty-state"><div className="empty-state-icon">🛒</div><h3>No orders yet</h3><p>Orders will appear here when customers book your services.</p></div>}
          </div>
        )}

        {/* Payments Tab */}
        {tab === 'payments' && (
          <div>
            <h3 className="mb-2">Payment & Payouts</h3>
            <div className="payments-grid">
              <div className="card p-3">
                <h4 className="mb-2">Stripe Payout Account</h4>
                {balance?.needsOnboarding ? (
                  <>
                    <p className="text-light mb-2">Connect Stripe to receive payouts directly to your bank account.</p>
                    <button className="btn btn-primary" onClick={handleStripeOnboarding} disabled={onboarding}>
                      {onboarding ? 'Opening Stripe...' : '💳 Connect Stripe Account'}
                    </button>
                  </>
                ) : (
                  <div>
                    <div className="alert alert-success">✅ Stripe account connected</div>
                    {balance?.balance && (
                      <div>
                        <p><strong>Available:</strong> ${((balance.balance.available?.[0]?.amount || 0) / 100).toFixed(2)}</p>
                        <p><strong>Pending:</strong> ${((balance.balance.pending?.[0]?.amount || 0) / 100).toFixed(2)}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="card p-3">
                <h4 className="mb-2">Subscription Plan</h4>
                <div className="subscription-plans">
                  {[
                    { tier: 'FREE', price: '$0/mo', limit: '3 services', label: 'Current (Free)' },
                    { tier: 'BASIC', price: '$9/mo', limit: '15 services', label: 'Basic' },
                    { tier: 'PREMIUM', price: '$29/mo', limit: '50 services', label: 'Premium' },
                  ].map(plan => (
                    <div key={plan.tier} className="plan-card">
                      <div className="plan-name">{plan.label}</div>
                      <div className="plan-price">{plan.price}</div>
                      <div className="plan-limit text-light text-sm">{plan.limit}</div>
                      {plan.tier !== 'FREE' && (
                        <button className="btn btn-outline btn-sm mt-1" onClick={() => paymentAPI.subscribe(plan.tier).then(r => window.location.href = r.data.url)}>
                          Upgrade
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
